import { BRANCH_PATTERN } from "./feature-env.model";

export interface BranchInputResult {
  /** The normalised branch name, absent when the input was empty or invalid. */
  readonly branch?: string;
  /** Why the input was rejected. Present only when `branch` is absent. */
  readonly error?: string;
}

const GITHUB_TREE_URL = /^(?:https?:\/\/)?(?:www\.)?github\.com\/[^/]+\/[^/]+\/tree\/(.+)$/i;
const GITHUB_PULL_URL = /^(?:https?:\/\/)?(?:www\.)?github\.com\/[^/]+\/[^/]+\/pull\/\d+$/i;

/**
 * Turns whatever the user pasted into a branch name.
 *
 * Accepts a bare branch, a `repo:branch` pair, or a GitHub tree URL. A pull
 * request URL is rejected explicitly rather than silently: it carries a number,
 * not a branch, and we have no way to resolve one to the other without an API
 * call and a credential.
 */
export function normalizeBranchInput(raw: string): BranchInputResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {};
  }

  if (GITHUB_PULL_URL.test(trimmed)) {
    return {
      error: "That is a pull request URL. Copy the branch name from the PR page instead.",
    };
  }

  const treeMatch = GITHUB_TREE_URL.exec(trimmed);

  // Reject URL-like input that is not a tree URL.
  if (!treeMatch && (trimmed.includes("://") || /^(?:[a-z0-9-]+\.)+[a-z]{2,}\//i.test(trimmed))) {
    return {
      error: "That does not look like a branch name. Paste the branch name, or a GitHub .../tree/<branch> URL.",
    };
  }

  const withoutUrl = treeMatch ? cleanTreeCapture(treeMatch[1]) : trimmed;

  // A `repo:branch` pair keeps only the branch — the repo is chosen by which
  // box you typed into, not by what you pasted.
  const separator = withoutUrl.indexOf(":");
  const branch =
    separator > 0 && /^[A-Za-z0-9._-]+$/.test(withoutUrl.slice(0, separator))
      ? withoutUrl.slice(separator + 1).trim()
      : withoutUrl;

  if (!branch) {
    return { error: "Enter a branch name." };
  }

  if (!BRANCH_PATTERN.test(branch)) {
    return {
      error: "Branch names may only contain letters, numbers, dot, underscore, slash and hyphen.",
    };
  }

  return { branch: toDeployedSlug(branch) };
}

/**
 * The deployment platform registers a pod under a slugified branch name, so
 * the cookie must carry the slug rather than the branch. Mirrors
 * backend-feature-env-deployment.yml:
 *   tr '/' '-' | tr '_' '-' | tr [:upper:] [:lower:]
 */
function toDeployedSlug(branch: string): string {
  return branch.replace(/[/_]/g, "-").toLowerCase();
}

function cleanTreeCapture(captured: string): string {
  // Cut at first query string or fragment.
  const queryOrFragmentIndex = captured.search(/[?#]/);
  const withoutQuery = queryOrFragmentIndex >= 0 ? captured.slice(0, queryOrFragmentIndex) : captured;
  // Strip trailing slashes before decoding.
  const withoutTrailingSlash = withoutQuery.replace(/\/+$/, "");
  const decoded = safeDecode(withoutTrailingSlash);
  // Strip trailing slashes again after decoding (e.g., "feat%2F" → "feat/").
  return decoded.replace(/\/+$/, "");
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // Malformed percent-encoding — fall back to the raw text and let the
    // character check below reject it if it is genuinely invalid.
    return value;
  }
}
